import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner, ViewportSnapshot, ViewportStore } from "@slot/presentation";
import { artResolution } from "../../scene/artResolution";
import { BONUS_TRANSITION_LAYOUT } from "../../scene/layout";
import { BONUS_TRANSITION_TUNING } from "../../tuning/panelTuning";

/**
 * The artist's bonus transition (`bonus_transition.riv`, played as its Spine conversion): its `play` trigger plays a
 * red-orange potion explosion with the gold coins once (2 s): the screen dims, the magic and the coins gather in its
 * centre, the potion bursts and floods the screen, covering it wholly from its frame 46 to 62, then fades into smoke and
 * sparkles. Idle, and once it is over, it draws nothing.
 */
export const BONUS_TRANSITION_RIVE = { play: "play" } as const;

/** What the transition asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface BonusTransitionArt {
  readonly texture: Texture;
  resize(scale: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/** A play's two moments: the screen covered, and the play over. */
interface Play {
  covered: Promise<void>;
  over: Promise<void>;
  cover: () => void;
  end: () => void;
}

function newPlay(): Play {
  let cover = (): void => undefined;
  let end = (): void => undefined;
  const covered = new Promise<void>((resolve) => (cover = resolve));
  const over = new Promise<void>((resolve) => (end = resolve));
  return { covered, over, cover, end };
}

/**
 * The way into the bonus (the artist's handoff, 2026-10-13): the explosion over the whole screen, its square artboard
 * centred on the screen's centre (the overlays' origin) and covering it, drawn at the tuning's share of the screen's
 * pixels. `play` plays it once on the panels' runner; what goes on under it waits for its moments (`whenCovered`,
 * `whenOver`). Nothing plays before it is in.
 */
export class BonusTransition extends Container {
  private readonly runner: PresentationTaskRunner;
  private readonly log: Log;
  private readonly unsubscribe: () => void;
  private viewport: ViewportSnapshot;
  private art: BonusTransitionArt | null = null;
  private sprite: Sprite | null = null;
  private plays = 0;
  /** The play in flight, null when none is. */
  private current: Play | null = null;

  public constructor(input: { viewport: ViewportStore; runner: PresentationTaskRunner; log: Log }) {
    super({ label: "BonusTransition" });
    this.runner = input.runner;
    this.log = input.log;
    this.viewport = input.viewport.getSnapshot();
    this.unsubscribe = input.viewport.subscribe((viewport) => {
      this.viewport = viewport;
      this.fit();
    });
  }

  /** The artist's explosion, loaded: over the whole screen, hidden until it plays. */
  public setArt(art: BonusTransitionArt): void {
    if (this.destroyed || this.art) {
      art.dispose();
      return;
    }
    const sprite = new Sprite({ texture: art.texture, anchor: 0.5, label: "BonusTransitionArt" });
    sprite.visible = false;
    this.addChild(sprite);
    this.art = art;
    this.sprite = sprite;
    this.fit();
    this.log("[BonusTransition] the bonus transition is in");
  }

  /** Whether it plays now. */
  public get playing(): boolean {
    return this.current !== null;
  }

  /** Whether it is in, how many times it has played, and whether it plays now (the dev hook's read). */
  public get state(): { loaded: boolean; plays: number; playing: boolean } {
    return { loaded: this.art !== null, plays: this.plays, playing: this.playing };
  }

  /** Plays it once over the screen, from its start; resolves once it has gone (at once before it is in). */
  public play(): Promise<void> {
    const { art, sprite } = this;
    if (!art || !sprite || this.destroyed) return Promise.resolve();
    this.finish();
    const play = newPlay();
    this.current = play;
    this.plays += 1;
    art.trigger(BONUS_TRANSITION_RIVE.play);
    art.render(0);
    sprite.visible = true;
    const { seconds, swapSeconds } = BONUS_TRANSITION_TUNING;
    const current = (): boolean => this.current === play && !this.destroyed;
    let last = 0;
    void this.runner.tickInScope(
      "",
      (elapsed) => {
        if (!current()) return;
        art.advance(elapsed - last);
        last = elapsed;
        if (elapsed >= swapSeconds) play.cover();
      },
      () => !current() || last >= seconds,
    ).then(() => {
      if (current()) this.finish();
    });
    return play.over;
  }

  /** Once the play in flight covers the screen wholly (the tuning's swap): what it reveals is seated under it. At once when none is. */
  public whenCovered(): Promise<void> {
    return this.current?.covered ?? Promise.resolve();
  }

  /** Once the play in flight is over and the screen shows again. At once when none is. */
  public whenOver(): Promise<void> {
    return this.current?.over ?? Promise.resolve();
  }

  /** Hidden at once, a play in flight ended where it is (whatever waits for it goes on). */
  public stop(): void {
    this.finish();
  }

  public override destroy(options?: DestroyOptions): void {
    this.finish();
    this.unsubscribe();
    const art = this.art;
    this.art = null;
    this.sprite = null;
    art?.dispose();
    super.destroy(options);
  }

  /** The play in flight ended: hidden, and both its moments passed. */
  private finish(): void {
    const play = this.current;
    this.current = null;
    if (this.sprite) this.sprite.visible = false;
    play?.cover();
    play?.end();
  }

  /** Its square over the whole screen (the larger side), its texture at the tuning's share of the screen's pixels. */
  private fit(): void {
    const { art, sprite } = this;
    if (!art || !sprite) return;
    const { design, scale, shape } = this.viewport;
    const side = Math.max(design.width, design.height);
    sprite.setSize(side, side);
    art.resize((side / BONUS_TRANSITION_LAYOUT.artboard.width) * scale * artResolution(shape) * BONUS_TRANSITION_TUNING.resolution);
  }
}
